
import { Component, Vue, Watch } from 'vue-property-decorator'
import { switchAccount } from '@/auth/session'
import {
	acknowledge,
	fetchLinkRecipient,
	isAcknowledged,
	readIntendedRecipient,
	SHARED_LINK_USER_PARAM
} from '@/utils/sharedLink'

/**
 * Shared radar emails deep-link with `?user=<recipient uuid>`. That hint only steers the
 * LOGGED-OUT auth bootstrap, so a browser already signed in as a colleague keeps its own
 * session and lands on a radar page that legitimately shows nothing. Detect the mismatch
 * and offer to sign in as the account the link was addressed to.
 */
@Component
export default class WrongAccountDialog extends Vue {
	open = false
	intendedUuid: string | null = null
	maskedEmail: string | null = null
	companyName: string | null = null

	mounted(): void {
		this.evaluate()
	}

	@Watch('$route')
	onRouteChanged(): void {
		this.evaluate()
	}

	get currentUuid(): string | null {
		// @ts-ignore [vuex module state is untyped]
		return this.$store.state.auth.user.id || null
	}

	get currentEmail(): string | null {
		// @ts-ignore [vuex module state is untyped]
		return this.$store.state.auth.user.email || null
	}

	get description(): string {
		if (this.maskedEmail && this.companyName) {
			return this.$t('wrongAccount.descriptionWithCompany', {
				email: this.maskedEmail,
				company: this.companyName
			}).toString()
		}
		if (this.maskedEmail) {
			return this.$t('wrongAccount.description', { email: this.maskedEmail }).toString()
		}
		return this.$t('wrongAccount.descriptionUnknownRecipient').toString()
	}

	get signedInAs(): string {
		if (!this.currentEmail) return this.$t('wrongAccount.signedInAsUnknown').toString()
		return this.$t('wrongAccount.signedInAs', { email: this.currentEmail }).toString()
	}

	async evaluate(): Promise<void> {
		const uuid = readIntendedRecipient(this.$route.query)
		if (!uuid || !this.currentUuid || uuid === this.currentUuid || isAcknowledged(uuid)) return

		this.intendedUuid = uuid
		this.maskedEmail = null
		this.companyName = null
		this.open = true

		const recipient = await fetchLinkRecipient(uuid)
		// A later navigation may already have resolved/dismissed this one.
		if (!recipient || this.intendedUuid !== uuid) return
		this.maskedEmail = recipient.maskedEmail
		this.companyName = recipient.companyName
	}

	switchAccount(): void {
		switchAccount({ returnTo: window.location.href, user: this.intendedUuid })
	}

	continueAsCurrentUser(): void {
		if (this.intendedUuid) acknowledge(this.intendedUuid)
		this.open = false
		this.intendedUuid = null

		const query = { ...this.$route.query }
		delete query[SHARED_LINK_USER_PARAM]
		this.$router.replace({ path: this.$route.path, query, hash: this.$route.hash }).catch(() => {})
	}
}
